import { Form } from 'components/form'
import { useAlert } from 'contexts/alert'
import { ICustomConnectorDTO } from 'data/dtos/automation/i-custom-connector-dto'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import api from 'services/api'

import { ajvResolver } from '@hookform/resolvers/ajv'
import CustomConnectorSchema, { ICustomConnectorFormData } from 'schema/automation/custom-connector'
import { useStyles } from './styles'

interface IRouteParams {
  id: string
}

const CustomConnectorForm: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [readonly, setReadonly] = useState(false)
  const [connectorTypesA, setConnectorTypesA] = useState([])
  const { closeAlert, openAlert } = useAlert()
  const params = useParams<IRouteParams>()
  const firstInputElement = useRef(null)
  const classes = useStyles()
  const history = useHistory()

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
    control,
    setFocus,
  } = useForm<ICustomConnectorFormData>({
    resolver: ajvResolver(CustomConnectorSchema),
    defaultValues: {
      connectorTypeId: '',
      connectorName: '',
      connectorJson: '',
      inputParams: '',
      outputParams: '',
      isInactive: false,
    },
  })

  const handleChange = (formField: any) => closeAlert()

  // main data load
  useEffect(() => {
    async function loadData() {
      const { id } = params

      // form data
      await api
        .get(`/custom-connectors/${id}`)
        .then((response) => {
          const { data } = response.data

          const customConnectorResult = {
            connectorTypeId: data.connectorTypeId.id,
            connectorName: data.connectorName,
            connectorJson: data.connectorJson,
            inputParams: data.inputParams,
            outputParams: data.outputParams,
            isInactive: data.isInactive,
          }

          return customConnectorResult
        })
        .then((customConnectorResult: ICustomConnectorDTO) => {
          reset(customConnectorResult)
        })
        .catch((error) => {
          console.log(error)
          return error
        })
    }

    if (params.id) {
      loadData()
      setReadonly(true)
    }
  }, [params, params.id])

  // data save
  const onSubmit = useCallback(async (data: ICustomConnectorDTO) => {
    setIsLoading(true)

    const payLoad: ICustomConnectorDTO = {
      connectorTypeId: data.connectorTypeId,
      connectorName: data.connectorName,
      connectorJson: data.connectorJson,
      inputParams: data.inputParams,
      outputParams: data.outputParams,
      isInactive: data.isInactive,
    }

    if (params.id) {
      const { id } = params

      payLoad.id = id

      await api
        .put(`/custom-connectors`, payLoad)
        .then(() => {
          openAlert('Cliente atualizado com sucesso!', 'success')
          history.push('/custom-connectors')
        })
        .catch((error) => {
          openAlert(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!', 'error')
        })
        .finally(() => setIsLoading(false))
    } else {
      await api
        .post('/custom-connectors', payLoad)
        .then(() => {
          openAlert('Dado cadastrado com sucesso!', 'success')
          reset()
        })
        .catch((error) => {
          openAlert(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!', 'error')
        })
        .finally(() => setIsLoading(false))
    }
  }, [])

  return (
    <Form.Root onHandleSubmit={handleSubmit(onSubmit)}>
      <Form.Info
        title='Conectores Customizados'
        subTitle='Dados sobre os conectores de uso por todos.'
      />

      <Form.Body>
        <Form.Select
          label='Tipo de Conector'
          error={!!errors.connectorTypeId}
          errorMessage={errors?.connectorTypeId?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
          name='connectorTypeId'
          rules={{
            onChange: (e) => {
              setValue('connectorTypeId', e.target.value)
              handleChange(e)
            },
          }}
          register={register}
          value={`${watch().connectorTypeId}`}
          options={connectorTypesA}
        />

        <Form.Input
          label='Nome do conector'
          error={!!errors.connectorName}
          errorMessage={errors?.connectorName?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
          maxLength={100}
          name='connectorName'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.Input
          label='JSON do Connector'
          error={!!errors.connectorJson}
          errorMessage={errors?.connectorJson?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          maxLength={4096}
          name='connectorJson'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.Input
          label='Parâmetros de Entrada'
          error={!!errors.inputParams}
          errorMessage={errors?.inputParams?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          maxLength={4096}
          name='inputParams'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.Input
          label='Parâmetros de Saída'
          error={!!errors.outputParams}
          errorMessage={errors?.outputParams?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          maxLength={4096}
          name='outputParams'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.CheckBox
          label='Inativo'
          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          name='isInactive'
          control={control}
        />
      </Form.Body>

      <Form.Divisor />

      <Form.Footer>
        <Form.Button
          label='SALVAR'
          type='submit'
          variant='contained'
          isLoading={isLoading}
        />
        <Form.Button
          label='CANCELAR'
          type='cancel'
          variant='text'
          isLoading={isLoading}
        />
      </Form.Footer>
    </Form.Root>
  )
}

export default CustomConnectorForm
